import { Injectable } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { ToastAction, ToastData, ToastType } from './toast.model';
import { ToastComponent } from './toast.component';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private currentRef: MatSnackBarRef<ToastComponent> | null = null;
  private currentImportant = false;

  constructor(private snackBar: MatSnackBar) {}

  open(
    message: string,
    type: ToastType = 'info',
    actions: ToastAction[] = [],
    showCloseButton: boolean = true,
    important: boolean = false,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<ToastComponent> | null {
    if (this.currentImportant) {
      return null;
    }

    const data: ToastData = {
      message,
      type,
      actions,
      showCloseButton,
      important,
    };

    const hasActions = actions?.length > 0;

    const ref = this.snackBar.openFromComponent(ToastComponent, {
      data,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      duration: hasActions ? undefined : 3000,
      panelClass: ['toast-panel'],
      ...config,
    });

    this.currentRef = ref;
    this.currentImportant = important;

    ref.afterDismissed().subscribe(() => {
      if (this.currentRef === ref) {
        this.currentRef = null;
        this.currentImportant = false;
      }
    });

    return ref;
  }

  dismissCurrent(): void {
    this.currentRef?.dismiss();
  }

  success(
    message: string,
    actions: ToastAction[] = [],
    showCloseButton: boolean = true,
    important: boolean = false,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<ToastComponent> | null {
    return this.open(
      message,
      'success',
      actions,
      showCloseButton,
      important,
      config,
    );
  }

  warning(
    message: string,
    actions: ToastAction[] = [],
    showCloseButton: boolean = true,
    important: boolean = false,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<ToastComponent> | null {
    return this.open(
      message,
      'warning',
      actions,
      showCloseButton,
      important,
      config,
    );
  }

  info(
    message: string,
    actions: ToastAction[] = [],
    showCloseButton: boolean = true,
    important: boolean = false,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<ToastComponent> | null {
    return this.open(
      message,
      'info',
      actions,
      showCloseButton,
      important,
      config,
    );
  }

  error(
    message: string,
    actions: ToastAction[] = [],
    showCloseButton: boolean = true,
    important: boolean = false,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<ToastComponent> | null {
    return this.open(
      message,
      'error',
      actions,
      showCloseButton,
      important,
      config,
    );
  }
}
